export const SHAPES = {
  rectangle: {
    label: 'Rectangle',
    fields: [{ key: 'length', label: 'Length (m)' }, { key: 'width', label: 'Width (m)' }],
    area: ({ length, width }) => (length || 0) * (width || 0),
    defaults: { length: 10, width: 6 },
  },
  lshape: {
    label: 'L-shape',
    fields: [{ key: 'length', label: 'Long side (m)' }, { key: 'width', label: 'Top width (m)' }, { key: 'legLength', label: 'Leg length (m)' }, { key: 'legWidth', label: 'Leg width (m)' }],
    area: ({ length, width, legLength, legWidth }) => (length || 0) * (width || 0) + (legLength || 0) * (legWidth || 0),
    defaults: { length: 12, width: 5, legLength: 5, legWidth: 4 },
  },
  circle: {
    label: 'Circle',
    fields: [{ key: 'diameter', label: 'Diameter (m)' }],
    area: ({ diameter }) => Math.PI * ((diameter || 0) / 2) ** 2,
    defaults: { diameter: 6 },
  },
};

const num = (v) => (Number.isFinite(parseFloat(v)) && parseFloat(v) > 0 ? parseFloat(v) : 0);

export function shapeArea(type, values) {
  const parsed = Object.fromEntries(Object.entries(values).map(([k, v]) => [k, num(v)]));
  return SHAPES[type].area(parsed);
}

const Label = ({ x, y, text, anchor = 'middle' }) => (
  <text x={x} y={y} textAnchor={anchor} fontSize="10" fontWeight="600" fill="#1B4332">{text}</text>
);

export function ShapePreview({ type, values }) {
  const W = 180, H = 130, pad = 22;
  const v = Object.fromEntries(Object.entries(values).map(([k, val]) => [k, num(val)]));
  const fill = '#40916c', stroke = '#1B4332';

  if (type === 'circle') {
    const r = Math.min(W, H) / 2 - pad;
    return (
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-full" data-testid="shape-preview-circle">
        <circle cx={W / 2} cy={H / 2} r={r} fill={fill} fillOpacity="0.85" stroke={stroke} strokeWidth="2" />
        <line x1={W / 2 - r} y1={H / 2} x2={W / 2 + r} y2={H / 2} stroke="#fff" strokeDasharray="4 3" />
        <Label x={W / 2} y={H / 2 - 6} text={v.diameter ? `${v.diameter} m` : '— m'} />
      </svg>
    );
  }

  const totalL = type === 'lshape' ? Math.max(v.length, v.legLength) : v.length;
  const totalW = type === 'lshape' ? v.width + v.legWidth : v.width;
  if (!totalL || !totalW) {
    return <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-full"><rect x={pad} y={pad} width={W - pad * 2} height={H - pad * 2} fill="none" stroke="#c9c2b0" strokeDasharray="5 4" rx="6" /></svg>;
  }
  const scale = Math.min((W - pad * 2) / totalL, (H - pad * 2) / totalW);
  const ox = (W - totalL * scale) / 2, oy = (H - totalW * scale) / 2;
  const sx = (m) => ox + m * scale, sy = (m) => oy + m * scale;

  if (type === 'rectangle') {
    return (
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-full" data-testid="shape-preview-rectangle">
        <rect x={sx(0)} y={sy(0)} width={v.length * scale} height={v.width * scale} fill={fill} fillOpacity="0.85" stroke={stroke} strokeWidth="2" rx="2" />
        <Label x={sx(v.length / 2)} y={sy(0) - 6} text={`${v.length} m`} />
        <Label x={sx(0) - 6} y={sy(v.width / 2) + 4} text={`${v.width} m`} anchor="end" />
      </svg>
    );
  }

  const pts = [[0, 0], [v.length, 0], [v.length, v.width], [v.legLength, v.width], [v.legLength, v.width + v.legWidth], [0, v.width + v.legWidth]];
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-full" data-testid="shape-preview-lshape">
      <polygon points={pts.map(([x, y]) => `${sx(x)},${sy(y)}`).join(' ')} fill={fill} fillOpacity="0.85" stroke={stroke} strokeWidth="2" strokeLinejoin="round" />
      <Label x={sx(v.length / 2)} y={sy(0) - 6} text={`${v.length} m`} />
      <Label x={sx(v.length) + 6} y={sy(v.width / 2) + 4} text={`${v.width} m`} anchor="start" />
      <Label x={sx(0) - 6} y={sy(v.width + v.legWidth / 2) + 4} text={`${v.legWidth} m`} anchor="end" />
      <Label x={sx(v.legLength / 2)} y={sy(v.width + v.legWidth) + 12} text={`${v.legLength} m`} />
    </svg>
  );
}
